import React, { useCallback, useState } from 'react';
import { Markets } from '../../@types/configuration';
import { ExternalData } from '../../@types/external-api';
import environment from '../../config/env';
import { Features } from '../../config/features';
import {
  makeExternalCallErrorData,
  makeExternalDataInitialData,
  makeExternalDataInitialKeepData,
  makeExternalDataSuccessData,
} from '../../helpers/external-data';
import useExternalApiErrorHandler from '../../hooks/use-external-api-error-handler';
import useFeatures from '../features/features-hook';
import messagesService from '../messages/messages.service';
import ExecutionsContext, { ExecutionsCtxType } from './executions-context';
import {
  CreateProcessorExecutionResponse,
  ExecutionDetails,
  ExecutionsResponse,
  ProcessorStepLog,
} from './executions-types';
import executionsService from './executions.service';

const ExecutionProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const errorHandler = useExternalApiErrorHandler();
  const [createExecutionResponse, setCreateExecutionResponse] =
    useState<ExternalData<CreateProcessorExecutionResponse>>(makeExternalDataInitialData());
  const [executionDetails, setExecutionDetails] =
    useState<ExternalData<ExecutionDetails>>(makeExternalDataInitialData());
  const [executions, setExecutions] = useState<ExternalData<ExecutionsResponse>>(makeExternalDataInitialData());
  const [executionCompletedSteps, setExecutionsCompletedSteps] =
    useState<ExternalData<ProcessorStepLog[]>>(makeExternalDataInitialData());
  const [exceptions, setExceptions] = useState<ExternalData<ExecutionsResponse>>(makeExternalDataInitialData());
  const { hasFeature } = useFeatures();

  const resetExecution = () => {
    setCreateExecutionResponse(makeExternalDataInitialData());
    setExecutionDetails(makeExternalDataInitialData());
    setExecutionsCompletedSteps(makeExternalDataInitialData());
  };

  const createExecution: ExecutionsCtxType['createExecution'] = async req => {
    try {
      resetExecution();
      let data;
      const processorTopics: Record<string, string> = req.processorTopics!;
      delete req.processorTopics;
      // create a new event via new PublishAPI or KAFKA
      if (
        hasFeature(Features.MktFeature_UsesPublishAPI) &&
        isPublisherMarketOrProcessor(req.processorName, processorTopics)
      ) {
        data = await executionsService.createOnPublisherApi(req);
      } else {
        data = await executionsService.create(req);
      }
      setCreateExecutionResponse(makeExternalDataSuccessData(data));
    } catch (err: any) {
      setCreateExecutionResponse(makeExternalCallErrorData(err));
    }
  };

  const fetchExecutions: ExecutionsCtxType['fetchExecutions'] = useCallback(
    req => {
      const { abort, promise } = executionsService.getExecutionHistory(req);

      setExecutions(makeExternalDataInitialData());

      promise
        .then(data => {
          setExecutions(makeExternalDataSuccessData(data));
        })
        .catch(err => {
          setExecutions(makeExternalCallErrorData(err));
          errorHandler(err, {
            cancel: () => {
              console.log('axios request cancelled', err.message);
              setExecutions(makeExternalDataInitialData());
            },
          });
        });

      return [abort];
    },
    [errorHandler],
  );

  const fetchExceptions: ExecutionsCtxType['fetchExceptions'] = useCallback(
    req => {
      const { abort, promise } = executionsService.getExecutionHistory(req);

      setExceptions(prev => makeExternalDataInitialKeepData(prev, abort));

      promise
        .then(data => {
          if (req.marketHasCustomChannel) {
            const { promise } = messagesService.getMessageExceptions({
              startDt: req.startDate,
              endDt: req.endDate,
              executionIds: data.executions.map(pl => pl.executionId!),
            });
            promise.then(cc_exceptions => {
              const exceptionsWithCustomChannelFlag = data;
              if (exceptionsWithCustomChannelFlag) {
                exceptionsWithCustomChannelFlag.executions.forEach(execution => {
                  execution.hasCustomChannelMessage = execution.executionId
                    ? cc_exceptions.exceptions.some(e => execution.executionId === e.executionId)
                    : false;
                  execution.customChannelId = cc_exceptions.exceptions.find(
                    e => e.executionId === execution.executionId,
                  )?.customChannelId;
                });
                setExceptions(makeExternalDataSuccessData(exceptionsWithCustomChannelFlag));
              }
            });
          } else {
            setExceptions(makeExternalDataSuccessData(data));
          }
        })
        .catch(err => {
          setExceptions(makeExternalCallErrorData(err));
          errorHandler(err, {
            cancel: () => {
              console.log('axios request cancelled', err.message);
              setExceptions(makeExternalDataInitialData());
            },
          });
        });

      return abort;
    },
    [errorHandler],
  );

  const fetchExecutionDetails: ExecutionsCtxType['fetchExecutionDetails'] = useCallback(async req => {
    try {
      setExecutionDetails(makeExternalDataInitialData());
      const data = await executionsService.getExecutionDetails(req);
      setExecutionDetails(makeExternalDataSuccessData(data));
    } catch (err: any) {
      setExecutionDetails(makeExternalCallErrorData(err));
    }
  }, []);

  const fetchExecutionCompletedSteps = useCallback(async (executionId: string) => {
    try {
      const data = await executionsService.getExecutionCompletedSteps(executionId);
      setExecutionsCompletedSteps(makeExternalDataSuccessData(data));
    } catch (err: any) {
      setExecutionDetails(makeExternalCallErrorData(err));
    }
  }, []);

  function isPublisherMarketOrProcessor(processorName: string, processorTopics?: Record<string, string>): boolean {
    return (
      environment.MARKET === Markets.KOREA ||
      (processorTopics != null &&
        processorTopics[processorName] != null &&
        processorTopics[processorName].includes(`${environment.MARKET.toLowerCase()}-publisher`))
    );
  }

  return (
    <ExecutionsContext.Provider
      value={{
        createExecutionResponse,
        executions,
        exceptions,
        executionDetails,
        executionCompletedSteps,
        createExecution,
        fetchExecutions,
        fetchExceptions,
        fetchExecutionDetails,
        fetchExecutionCompletedSteps,
        resetExecution,
      }}>
      {children}
    </ExecutionsContext.Provider>
  );
};

export default ExecutionProvider;
